import {
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  getFrozenRevision,
  isCardFrozen,
  isActivatingCardStatus,
  isBlockedCardStatus,
  isCardLimitReached,
  setSelectedCardId,
  subscribeCardUpdates,
} from '../../../assets/services/cards';
import {
  beginCarouselCardFlow,
  cancelCarouselCardFlow,
  consumeCarouselCardReturn,
  getPendingCarouselCardReturn,
} from '../../../assets/services/carouselReturn';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import BankCard from '../../BankCard/BankCard';
import './HomeCardCarousel.css';

const CAROUSEL_SIDE_OFFSET = 20;
const CAROUSEL_SPACE_BETWEEN = 20;

function AddCardTile({ disabled = false, label, onClick }) {
  return (
    <button
      type="button"
      className={`home-carousel__add${disabled ? ' home-carousel__add--disabled' : ''}`}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="home-carousel__add-icon" aria-hidden="true">
        +
      </span>
      <span className="home-carousel__add-label">{label}</span>
    </button>
  );
}

function resolveSelectedIndex(cards, selectedId) {
  const index = cards.findIndex((card) => card.id === selectedId);
  if (index >= 0) {
    return index;
  }
  return cards.length > 0 ? 0 : -1;
}

function isPendingCard(card) {
  return card.status === 'not_issued' || card.status === 'pending';
}

function HomeCardCarousel({
  cards,
  selectedId,
  onSelectedChange,
  showAddTile = true,
}) {
  const navigate = useNavigate();
  const { currentUser } = useContext(CurrentUserContext);
  const { card: cardCopy, home } = useContext(TranslationContext);
  const swiperRef = useRef(null);
  const restoreTargetIdRef = useRef(selectedId || getPendingCarouselCardReturn());
  const hasRestoredPositionRef = useRef(false);
  const restoredSelectedId = restoreTargetIdRef.current;
  const initialSlide = Math.max(0, resolveSelectedIndex(cards, restoredSelectedId));
  const [activeIndex, setActiveIndex] = useState(initialSlide);
  const slideCount = showAddTile ? cards.length + 1 : cards.length;
  useSyncExternalStore(
    subscribeCardUpdates,
    getFrozenRevision,
    () => 0
  );
  const hasIssueInProgress = cards.some((card) => (
    isPendingCard(card) || isActivatingCardStatus(card.status)
  ));
  const hasReachedCardLimit = isCardLimitReached(cards, currentUser);
  const isAddCardDisabled = hasIssueInProgress || hasReachedCardLimit;
  const addCardLabel = hasIssueInProgress
    ? home.cardIssueInProgress
    : hasReachedCardLimit
      ? home.cardLimitReached
      : home.addNewCard;

  useEffect(() => {
    if (hasRestoredPositionRef.current) return undefined;

    if (!restoredSelectedId) {
      hasRestoredPositionRef.current = true;
      cancelCarouselCardFlow();
      return undefined;
    }

    const index = cards.findIndex((card) => card.id === restoredSelectedId);
    if (index < 0) return undefined;

    hasRestoredPositionRef.current = true;
    consumeCarouselCardReturn(restoredSelectedId);
    setActiveIndex(index);

    if (swiperRef.current && swiperRef.current.activeIndex !== index) {
      swiperRef.current.slideTo(index, 0);
    }

    return undefined;
  }, [cards, restoredSelectedId]);

  const handleSlideChange = useCallback(
    (swiper) => {
      const nextIndex = swiper.activeIndex;
      setActiveIndex(nextIndex);

      if (nextIndex < cards.length) {
        const nextCardId = cards[nextIndex].id;
        setSelectedCardId(nextCardId);
        onSelectedChange?.(nextCardId);
      } else if (showAddTile) {
        onSelectedChange?.(null);
      }
    },
    [cards, onSelectedChange, showAddTile]
  );

  const goToSlide = useCallback((index) => {
    if (index < 0 || index >= slideCount) return;

    setActiveIndex(index);
    swiperRef.current?.slideTo(index);
  }, [slideCount]);

  const handleSwiper = useCallback((swiper) => {
    swiperRef.current = swiper;
  }, []);

  const handleCardClick = useCallback(
    (event, card) => {
      if (swiperRef.current && swiperRef.current.allowClick === false) {
        return;
      }

      triggerImpactHaptic('light');
      beginCarouselCardFlow(card.id);
      setSelectedCardId(card.id);
      const sourceRect = event.currentTarget.getBoundingClientRect();

      navigate(`/cards/${card.id}`, {
        state: {
          skipRouteOverlayTransition: true,
          cardTransitionFrom: {
            left: sourceRect.left,
            top: sourceRect.top,
            width: sourceRect.width,
            height: sourceRect.height,
          },
        },
      });
    },
    [navigate]
  );

  const renderTransitionCard = useCallback(
    ({ card, frozen, blocked, activating }) => (
      <BankCard
        cardStyle={card.style}
        cardType={card.type}
        showCardType
        size="carousel"
        label={card.label}
        balance={card.balance}
        detailsText={card.detailsText}
        heldBalance={card.uncreditedBalance}
        lastFour={card.lastFour}
        frozen={frozen}
        pending={isPendingCard(card)}
        blocked={blocked}
        activating={activating}
        animatePending={false}
      />
    ),
    []
  );

  return (
    <div className="home-carousel home-carousel--ready">
      <Swiper
        className="home-carousel__swiper"
        slidesPerView="auto"
        spaceBetween={CAROUSEL_SPACE_BETWEEN}
        slidesOffsetBefore={CAROUSEL_SIDE_OFFSET}
        slidesOffsetAfter={CAROUSEL_SIDE_OFFSET}
        initialSlide={initialSlide}
        onSwiper={handleSwiper}
        onSlideChange={handleSlideChange}
        watchOverflow
      >
        {cards.map((card, index) => {
          const blocked = isBlockedCardStatus(card.status);
          const activating = isActivatingCardStatus(card.status);
          const frozen = !blocked && !activating && isCardFrozen(card.id);
          const hasStableStatus = frozen || isPendingCard(card) || activating || blocked;

          return (
            <SwiperSlide
              key={card.id}
              className={[
                'home-carousel__slide',
                index === activeIndex ? 'home-carousel__slide--active' : null,
                frozen ? 'home-carousel__slide--frozen' : null,
                blocked ? 'home-carousel__slide--blocked' : null,
                activating ? 'home-carousel__slide--activating' : null,
                hasStableStatus ? 'home-carousel__slide--stable-status' : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <button
                type="button"
                className="home-carousel__card-button"
                aria-label={`${cardCopy.myCard}: ${card.label}`}
                onClick={(event) => handleCardClick(event, card)}
              >
                {renderTransitionCard({
                  card,
                  frozen,
                  blocked,
                  activating,
                })}
              </button>
            </SwiperSlide>
          );
        })}
        {showAddTile && (
          <SwiperSlide
            className={
              activeIndex === cards.length
                ? 'home-carousel__slide home-carousel__slide--active'
                : 'home-carousel__slide'
            }
          >
            <AddCardTile
              disabled={isAddCardDisabled}
              label={addCardLabel}
              onClick={() => navigate('/cards/new')}
            />
          </SwiperSlide>
        )}
      </Swiper>

      <div className="home-carousel__dots" role="tablist" aria-label={home.cards}>
        {Array.from({ length: slideCount }, (_, index) => (
          <button
            key={index}
            type="button"
            role="tab"
            aria-selected={index === activeIndex}
            aria-label={
              index < cards.length
                ? `${cardCopy.myCard}: ${cards[index].label}`
                : addCardLabel
            }
            className={
              index === activeIndex
                ? 'home-carousel__dot home-carousel__dot--active'
                : 'home-carousel__dot'
            }
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
}

export default HomeCardCarousel;
